import { DefaultError, useMutation, useQuery } from "@tanstack/react-query";
import { useQueryToast } from "hooks";
import i18next from "i18next";
import {
  INotificationSettingsQuery,
  UserSpecificationResponse,
  UserSpecificationsResponse,
} from "services/configuration/notifications/types";
import { apiClient } from "utils";
import { useResponseHandler } from "utils/service/responseHandler";

export const useGetUserSpecification = () => {
  const { responseHandler } = useResponseHandler();
  return useQuery<UserSpecificationResponse, DefaultError>({
    queryKey: ["notification"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: "Notification/UserSpecification",
      });

      return responseHandler(response, {
        groupName: "useGetUserSpecification",
      });
    },
  });
};

export const useGetUserSpecifications = () => {
  const { responseHandler } = useResponseHandler();

  return useQuery<UserSpecificationsResponse, DefaultError>({
    queryKey: ["notifications"],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: "Notification/UserSpecifications",
      });

      return responseHandler(response, {
        groupName: "useGetUserSpecifications",
      });
    },
  });
};

export const useDeleteUserSpecification = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useDeleteUserSpecification"
  );

  return useMutation<unknown, DefaultError, void>({
    mutationFn: async () => {
      loading(
        i18next.t("service-code.remove.loading", {
          what: i18next.t("notifications.what"),
        })
      );

      const response = await apiClient({
        method: "DELETE",
        url: `Notification/UserSpecification`,
      });

      return responseHandler(response, {
        groupName: `useDeleteUserSpecification`,
      });
    },
    onSuccess: (data: unknown) => {
      success(
        i18next.t("service-message.remove.success", {
          what: i18next.t("notifications.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useCreateUserSpecification = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useCreateUserSpecification"
  );

  return useMutation<unknown, DefaultError, INotificationSettingsQuery[]>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-code.create.loading", {
          what: i18next.t("notifications.what"),
        })
      );

      const response = await apiClient({
        method: "POST",
        url: `Notification/UserSpecification`,
        body,
      });

      return responseHandler(response, {
        groupName: `useCreateUserSpecification`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t("service-message.create.success", {
          what: i18next.t("notifications.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateUserSpecification = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useUpdateUserSpecification"
  );

  return useMutation<unknown, DefaultError, INotificationSettingsQuery[]>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-code.update.loading", {
          what: i18next.t("notifications.what"),
        })
      );

      const response = await apiClient({
        method: "PUT",
        url: `Notification/UserSpecification`,
        body,
      });

      return responseHandler(response, {
        groupName: `useUpdateUserSpecification`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t("service-message.update.success", {
          what: i18next.t("notifications.what"),
        })
      );
    },
    onError: dismiss,
  });
};
